import { create } from 'zustand'
import { supabase } from '../lib/supabaseClient'

const TIMER_STORAGE_KEY = 'resultos-focus-timer-state'
const DEFAULT_TIMER_SECONDS = 5400

function readStoredTimerState() {
  if (typeof window === 'undefined') return {}

  try {
    const stored = JSON.parse(window.localStorage.getItem(TIMER_STORAGE_KEY) || 'null')
    if (!stored) return {}

    const timerMax = stored.timerMax || DEFAULT_TIMER_SECONDS
    const activeDur = stored.activeDur || Math.round(timerMax / 60)
    const timerEndAt = stored.timerEndAt || null
    const timerStartedAt = stored.timerStartedAt || null

    if (stored.timerRunning && timerEndAt) {
      return {
        activeDur,
        timerMax,
        timerRunning: true,
        timerStartedAt,
        timerEndAt,
        timerSec: Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000)),
      }
    }

    return {
      activeDur,
      timerMax,
      timerRunning: false,
      timerStartedAt: null,
      timerEndAt: null,
      timerSec: stored.timerSec ?? timerMax,
    }
  } catch {
    return {}
  }
}

const initialTimerState = readStoredTimerState()

function persistTimerState(state) {
  if (typeof window === 'undefined') return

  window.localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({
    activeDur: state.activeDur,
    timerMax: state.timerMax,
    timerSec: state.timerSec,
    timerRunning: state.timerRunning,
    timerStartedAt: state.timerStartedAt,
    timerEndAt: state.timerEndAt,
  }))
}

const useStore = create((set, get) => ({
  // Auth state
  user: null,
  isLoading: true,

  // Burning desire — localStorage for instant display, DB for persistence across devices
  burningDesire: (typeof window !== 'undefined' ? localStorage.getItem('ros_burning_desire') : '') || '',

  // App state
  currentPanel: 'morning',
  history: [],
  monthlyRecord: null,
  selectedMonthNumber: new Date().getMonth() + 1,
  selectedYear: new Date().getFullYear(),
  timerSec: initialTimerState.timerSec ?? DEFAULT_TIMER_SECONDS,
  timerMax: initialTimerState.timerMax ?? DEFAULT_TIMER_SECONDS,
  timerRunning: initialTimerState.timerRunning ?? false,
  timerStartedAt: initialTimerState.timerStartedAt ?? null,
  timerEndAt: initialTimerState.timerEndAt ?? null,
  timerInterval: null,
  blocksToday: 0,
  activeDur: initialTimerState.activeDur ?? 90,
  focusMode: false,
  focusContextHidden: false,

  // Form data
  goal: '',
  monthlyGoal: '',
  wScore: 7,
  cScore: 0,       // auto-calculated from focus timer — do NOT set manually
  tScore: 7,
  reflection: '',

  // Focus tracking (for auto C calculation)
  focusMinutesTotal: 0,

  // Close The Loop state
  wShipped: null,
  loopBlocker: '',
  tomorrowSeed: '',
  closedAt: null,
  loopClosed: false,
  tScored: false,  // true once user explicitly scores T in Close The Loop

  // UI state
  syncState: 'ok',
  syncText: 'Synced',
  toast: null,

  // Actions
  setUser: (user) => set({ user, isLoading: false }),

  loadBurningDesire: async () => {
    const { user } = get()
    if (!user) return
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('burning_desire')
        .eq('user_id', user.id)
        .limit(1)
      if (error) throw error
      const value = data?.[0]?.burning_desire || ''
      // DB value always wins — update state and keep localStorage in sync
      if (typeof window !== 'undefined') {
        if (value) localStorage.setItem('ros_burning_desire', value)
        else localStorage.removeItem('ros_burning_desire')
      }
      set({ burningDesire: value })
    } catch (err) {
      console.error('Load burning desire error:', err)
      // Keep showing whatever localStorage had — better than showing nothing
    }
  },

  saveBurningDesire: async (text) => {
    const { user } = get()
    // Write to localStorage immediately — survives refresh even if DB is slow
    if (typeof window !== 'undefined') localStorage.setItem('ros_burning_desire', text)
    set({ burningDesire: text, syncState: 'busy', syncText: 'Saving...' })
    if (!user) { set({ syncState: 'ok', syncText: 'Saved' }); return }
    const payload = { user_id: user.id, burning_desire: text, updated_at: new Date().toISOString() }
    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert(payload, { onConflict: 'user_id' })

      if (error) {
        const conflictConstraintMissing = /no unique|on conflict|exclusion constraint/i.test(error.message || '')

        if (!conflictConstraintMissing) {
          throw error
        }

        const { data: existingRows, error: findError } = await supabase
          .from('user_settings')
          .select('user_id')
          .eq('user_id', user.id)
          .limit(1)

        if (findError) throw findError

        if (existingRows?.[0]?.user_id) {
          const { error: updateError } = await supabase
            .from('user_settings')
            .update({
              burning_desire: text,
              updated_at: payload.updated_at
            })
            .eq('user_id', user.id)

          if (updateError) throw updateError
        } else {
          const { error: insertError } = await supabase
            .from('user_settings')
            .insert(payload)

          if (insertError) throw insertError
        }
      }
      set({ syncState: 'ok', syncText: 'Synced' })
    } catch (err) {
      console.error('Save burning desire error:', err)
      set({ syncState: 'err', syncText: 'Error' })
      throw err
    }
  },

  setCurrentPanel: (panel) => set({ currentPanel: panel }),

  setMonthlySelection: ({ monthNumber, year }) => set((state) => ({
    selectedMonthNumber: monthNumber ?? state.selectedMonthNumber,
    selectedYear: year ?? state.selectedYear
  })),

  updateFormData: (data) => set((state) => ({ ...state, ...data })),

  setTimer: (updates) => set((state) => {
    const next = { ...state, ...updates }
    persistTimerState(next)
    return next
  }),

  setFocusMode: (focusMode) => set({ focusMode }),

  setFocusContextHidden: (focusContextHidden) => set({ focusContextHidden }),

  // minutes = actual active running minutes for this block (excludes paused time)
  addBlock: (minutes = 0) => set((state) => {
    const newMins = state.focusMinutesTotal + minutes
    return { blocksToday: state.blocksToday + 1, focusMinutesTotal: newMins, cScore: calcC(newMins) }
  }),

  // Log partial block minutes without incrementing blocksToday (called on manual stop/reset)
  logFocusMinutes: (minutes) => set((state) => {
    if (!minutes || minutes <= 0) return state
    const newMins = state.focusMinutesTotal + minutes
    return { focusMinutesTotal: newMins, cScore: calcC(newMins) }
  }),

  setSyncState: (state, text) => set({ syncState: state, syncText: text }),

  showToast: (icon, message) => {
    set({ toast: { icon, message } })
    setTimeout(() => set({ toast: null }), 3500)
  },

  // Auth actions
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    if (error) throw error
    return data
  },

  signUp: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    })
    if (error) throw error
    return data
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    if (typeof window !== 'undefined') localStorage.removeItem('ros_burning_desire')
    set({ user: null, history: [], monthlyRecord: null, monthlyGoal: '', burningDesire: '' })
  },

  resetPasswordForEmail: async (email) => {
    const redirectTo = `${window.location.origin}/auth`
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo
    })
    if (error) throw error
    return data
  },

  updatePassword: async (password) => {
    const { data, error } = await supabase.auth.updateUser({ password })
    if (error) throw error
    return data
  },

  submitFeedbackReport: async (report) => {
    const { user } = get()
    if (!user) throw new Error('Not signed in')

    const title = report.title?.trim()
    const details = report.details?.trim()

    if (!title || !details) {
      throw new Error('Title and details are required')
    }

    set({ syncState: 'busy', syncText: 'Sending...' })

    try {
      const { error } = await supabase
        .from('feedback_reports')
        .insert({
          user_id: user.id,
          user_email: user.email || null,
          contact_email: report.email?.trim() || user.email || null,
          phone_number: report.phone?.trim() || null,
          type: report.type,
          priority: report.priority,
          title,
          details,
          page_path: report.pagePath?.trim() || null,
          contact_ok: !!report.contactOk,
          status: 'new',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        })

      if (error) throw error

      set({ syncState: 'ok', syncText: 'Synced' })
    } catch (error) {
      console.error('Submit feedback error:', error)
      set({ syncState: 'err', syncText: 'Error' })
      throw error
    }
  },

  // Data actions
  loadHistory: async () => {
    const { user } = get()
    if (!user) return

    set({ syncState: 'busy', syncText: 'Loading...' })
    try {
      const { data, error } = await supabase
        .from('daily_scores')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(60)

      if (error) throw error

      set({ history: data || [], syncState: 'ok', syncText: 'Synced' })
    } catch (error) {
      console.error('Load history error:', error)
      set({ syncState: 'err', syncText: 'Error' })
      // Don't rethrow - handle gracefully
    }
  },

  loadMonthlyGoal: async (selection = {}) => {
    const { user } = get()
    if (!user) return

    const year = selection.year ?? get().selectedYear
    const monthNumber = selection.monthNumber ?? get().selectedMonthNumber

    set({ syncState: 'busy', syncText: 'Loading...' })
    try {
      const { data, error } = await supabase
        .from('monthly_results_areas')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', year)
        .eq('month_number', monthNumber)
        .limit(1)

      if (error) throw error

      const record = data?.[0] || null

      set({
        monthlyRecord: record,
        monthlyGoal: record?.monthly_goal || '',
        selectedMonthNumber: monthNumber,
        selectedYear: year,
        syncState: 'ok',
        syncText: 'Synced'
      })
    } catch (error) {
      console.error('Load monthly goal error:', error)
      set({ syncState: 'err', syncText: 'Error' })
    }
  },

  getMonthlyGoalForSelection: async (selection = {}) => {
    const { user } = get()
    if (!user) return ''

    const year = selection.year ?? get().selectedYear
    const monthNumber = selection.monthNumber ?? get().selectedMonthNumber

    const { data, error } = await supabase
      .from('monthly_results_areas')
      .select('monthly_goal')
      .eq('user_id', user.id)
      .eq('year', year)
      .eq('month_number', monthNumber)
      .limit(1)

    if (error) throw error
    return data?.[0]?.monthly_goal?.trim() || ''
  },

  saveDay: async () => {
    const { user, goal, wScore, cScore, tScore, reflection, focusMinutesTotal } = get()
    if (!user) throw new Error('Not signed in')

    const rScore = (wScore * cScore) + tScore
    const grade = getGrade(rScore)

    set({ syncState: 'busy', syncText: 'Saving...' })

    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          goal: goal || 'No goal recorded',
          w_score: wScore,
          c_score: cScore,
          t_score: tScore,
          r_score: rScore,
          grade,
          reflection,
          focus_minutes_total: focusMinutesTotal,
        }, { onConflict: 'user_id,date' })

      if (error) throw error

      set({ syncState: 'ok', syncText: 'Synced' })
      await get().loadHistory()
    } catch (error) {
      console.error('Save day error:', error)
      set({ syncState: 'err', syncText: 'Error' })
      throw error
    }
  },

  loadTodayLoop: async () => {
    const { user } = get()
    if (!user) return
    const today = new Date().toISOString().split('T')[0]
    try {
      const { data } = await supabase
        .from('daily_scores')
        .select('goal, w_score, w_shipped, blocker, tomorrow_seed, closed_at, focus_minutes_total, t_score, c_score, reflection')
        .eq('user_id', user.id)
        .eq('date', today)
        .limit(1)
      const row = data?.[0]
      const focusMins = row?.focus_minutes_total || 0

      let seedGoal = ''
      // If today has no goal yet, pull yesterday's tomorrow_seed as the starting point
      if (!row?.goal) {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayISO = yesterday.toISOString().split('T')[0]
        const { data: yd } = await supabase
          .from('daily_scores')
          .select('tomorrow_seed')
          .eq('user_id', user.id)
          .eq('date', yesterdayISO)
          .limit(1)
        seedGoal = yd?.[0]?.tomorrow_seed || ''
      }

      set({
        goal: row?.goal || seedGoal,
        wScore: row?.w_score ?? 7,
        wShipped: row?.w_shipped || null,
        loopBlocker: row?.blocker || '',
        tomorrowSeed: row?.tomorrow_seed || '',
        closedAt: row?.closed_at || null,
        loopClosed: !!row?.closed_at,
        focusMinutesTotal: focusMins,
        cScore: row?.c_score ?? calcC(focusMins),
        tScore: row?.t_score ?? 7,
        tScored: !!row?.closed_at,
        reflection: row?.reflection || '',
      })
    } catch (err) {
      console.error('Load today loop error:', err)
    }
  },

  closeLoop: async () => {
    const { user, goal, wScore, cScore, tScore, wShipped, loopBlocker, tomorrowSeed, focusMinutesTotal } = get()
    if (!user) throw new Error('Not signed in')
    const rScore = (wScore * cScore) + tScore
    const grade = getGrade(rScore)
    const now = new Date()
    const closedAt = now.toISOString()
    set({ syncState: 'busy', syncText: 'Saving...' })
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: now.toISOString().split('T')[0],
          goal: goal || 'No goal recorded',
          w_score: wScore,
          c_score: cScore,
          t_score: tScore,
          r_score: rScore,
          grade,
          w_shipped: wShipped,
          blocker: loopBlocker || null,
          tomorrow_seed: tomorrowSeed || null,
          closed_at: closedAt,
          focus_minutes_total: focusMinutesTotal,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
      set({ closedAt, loopClosed: true, tScored: true, syncState: 'ok', syncText: 'Synced' })
      await get().loadHistory()
    } catch (error) {
      console.error('Close loop error:', error)
      set({ syncState: 'err', syncText: 'Error' })
      throw error
    }
  },

  loadDayData: async (date) => {
    const { user } = get()
    if (!user) return
    set({ syncState: 'busy', syncText: 'Loading...' })
    try {
      const { data } = await supabase
        .from('daily_scores')
        .select('goal, w_score, w_shipped, blocker, tomorrow_seed, closed_at, focus_minutes_total, t_score, c_score, reflection')
        .eq('user_id', user.id)
        .eq('date', date)
        .limit(1)
      const row = data?.[0]
      const focusMins = row?.focus_minutes_total || 0
      set({
        goal: row?.goal || '',
        wScore: row?.w_score ?? 7,
        wShipped: row?.w_shipped || null,
        loopBlocker: row?.blocker || '',
        tomorrowSeed: row?.tomorrow_seed || '',
        closedAt: row?.closed_at || null,
        loopClosed: !!row?.closed_at,
        focusMinutesTotal: focusMins,
        cScore: row?.c_score ?? calcC(focusMins),
        tScore: row?.t_score ?? 7,
        tScored: !!row?.closed_at,
        reflection: row?.reflection || '',
        syncState: 'ok',
        syncText: 'Synced',
      })
    } catch (err) {
      console.error('Load day data error:', err)
      set({ syncState: 'err', syncText: 'Error' })
    }
  },

  saveGoalDraft: async () => {
    const { user, goal, wScore } = get()
    if (!user) return
    set({ syncState: 'busy', syncText: 'Saving...' })
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          goal: goal.trim() || 'No goal recorded',
          w_score: wScore,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
      set({ syncState: 'ok', syncText: 'Saved' })
    } catch (err) {
      console.error('Save goal draft error:', err)
      set({ syncState: 'err', syncText: 'Error' })
    }
  },

  saveLoopDraft: async () => {
    const { user, loopBlocker, tomorrowSeed } = get()
    if (!user) return
    set({ syncState: 'busy', syncText: 'Saving...' })
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          blocker: loopBlocker || null,
          tomorrow_seed: tomorrowSeed || null,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
      set({ syncState: 'ok', syncText: 'Saved' })
    } catch (err) {
      console.error('Save loop draft error:', err)
      set({ syncState: 'err', syncText: 'Error' })
    }
  },

  saveFocusDraft: async () => {
    const { user, focusMinutesTotal, cScore, tScore } = get()
    if (!user) return
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          focus_minutes_total: focusMinutesTotal,
          c_score: cScore,
          t_score: tScore,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
      set({ syncState: 'ok', syncText: 'Synced' })
    } catch (err) {
      console.error('Save focus draft error:', err)
    }
  },

  saveReflectionDraft: async () => {
    const { user, reflection } = get()
    if (!user) return
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          reflection: reflection || null,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
    } catch (err) {
      console.error('Save reflection draft error:', err)
    }
  },

  // Save a W goal for any date (future planning)
  saveDatePlan: async (date, { goal, wScore }) => {
    const { user } = get()
    if (!user) throw new Error('Not signed in')
    set({ syncState: 'busy', syncText: 'Saving...' })
    try {
      const { error } = await supabase
        .from('daily_scores')
        .upsert({
          user_id: user.id,
          date,
          goal: goal.trim() || 'No goal recorded',
          w_score: wScore,
        }, { onConflict: 'user_id,date' })
      if (error) throw error
      set({ syncState: 'ok', syncText: 'Saved' })
    } catch (err) {
      console.error('Save date plan error:', err)
      set({ syncState: 'err', syncText: 'Error' })
      throw err
    }
  },

  // Load saved goal for a specific date without touching global state
  loadDatePlan: async (date) => {
    const { user } = get()
    if (!user) return null
    try {
      const { data } = await supabase
        .from('daily_scores')
        .select('goal, w_score, tomorrow_seed')
        .eq('user_id', user.id)
        .eq('date', date)
        .limit(1)
      return data?.[0] ? { goal: data[0].goal || '', wScore: data[0].w_score ?? 7, tomorrowSeed: data[0].tomorrow_seed || '' } : null
    } catch {
      return null
    }
  },

  saveMonthlyGoal: async (selection = {}) => {
    const { user, monthlyGoal, monthlyRecord } = get()
    if (!user) throw new Error('Not signed in')

    const year = selection.year ?? get().selectedYear
    const monthNumber = selection.monthNumber ?? get().selectedMonthNumber
    const monthName = new Date(year, monthNumber - 1, 1).toLocaleString('en-US', { month: 'long' })
    const trimmedGoal = monthlyGoal.trim()
    const payload = {
      user_id: user.id,
      month_name: monthName,
      month_number: monthNumber,
      year,
      monthly_goal: trimmedGoal
    }

    set({ syncState: 'busy', syncText: 'Saving...' })

    try {
      const { error } = await supabase
        .from('monthly_results_areas')
        .upsert(payload, { onConflict: 'user_id,year,month_number' })

      if (error) {
        const conflictConstraintMissing = /no unique|on conflict|exclusion constraint/i.test(error.message || '')

        if (!conflictConstraintMissing) {
          throw error
        }

        const existingId = monthlyRecord?.id

        if (existingId) {
          const { error: updateError } = await supabase
            .from('monthly_results_areas')
            .update({
              month_name: monthName,
              month_number: monthNumber,
              year,
              monthly_goal: trimmedGoal
            })
            .eq('id', existingId)
            .eq('user_id', user.id)

          if (updateError) throw updateError
        } else {
          const { data: existingRows, error: findError } = await supabase
            .from('monthly_results_areas')
            .select('id')
            .eq('user_id', user.id)
            .eq('year', year)
            .eq('month_number', monthNumber)
            .limit(1)

          if (findError) throw findError

          if (existingRows?.[0]?.id) {
            const { error: updateError } = await supabase
              .from('monthly_results_areas')
              .update({
                month_name: monthName,
                month_number: monthNumber,
                year,
                monthly_goal: trimmedGoal
              })
              .eq('id', existingRows[0].id)
              .eq('user_id', user.id)

            if (updateError) throw updateError
          } else {
            const { error: insertError } = await supabase
              .from('monthly_results_areas')
              .insert(payload)

            if (insertError) throw insertError
          }
        }
      }

      set({
        monthlyRecord: {
          ...(monthlyRecord || {}),
          ...payload
        },
        monthlyGoal: trimmedGoal,
        selectedMonthNumber: monthNumber,
        selectedYear: year,
        syncState: 'ok',
        syncText: 'Synced'
      })
    } catch (error) {
      console.error('Save monthly goal error:', error)
      set({ syncState: 'err', syncText: 'Error' })
      throw error
    }
  }
}))

// C auto-calculation: linear interpolation across focus-minute breakpoints
// 0→0, 45→2, 90→4, 180→6, 270→8, 360+→10
function calcC(minutes) {
  if (!minutes || minutes <= 0) return 0
  if (minutes >= 360) return 10
  const bp = [[0,0],[45,2],[90,4],[180,6],[270,8],[360,10]]
  for (let i = 1; i < bp.length; i++) {
    if (minutes <= bp[i][0]) {
      const t = (minutes - bp[i-1][0]) / (bp[i][0] - bp[i-1][0])
      return Math.round(bp[i-1][1] + t * (bp[i][1] - bp[i-1][1]))
    }
  }
  return 10
}

function getGrade(score) {
  if (score >= 100) return 'S'
  if (score >= 80) return 'A'
  if (score >= 60) return 'B'
  if (score >= 40) return 'C'
  return 'D'
}

export default useStore
